import normalize from 'json-api-normalize';
import { MediaPathHelper } from '../helpers/media-path.helper';
import { DrupalBaseModel } from './drupal-base.model';
import { IndividualModel } from './individual.model';
import { InstitutionModel } from './institution.model';
import { LabelValueModel } from './label-value.model';
import { UserModel } from './user.model';

export class ProgramModel extends DrupalBaseModel {

  public static readonly NOT_AFF_US_GOVT = 'Not affiliated with US Government';

  public static parseProgram(programJson: any, baseImageUrl: string): ProgramModel {

    const normalizedData = normalize(programJson).get([
      'id',
      'title',
      'changed',
      'field_program_id',
      'field_address',
      'field_expanded_listing',
      'field_specialty.title',
      'field_specialty.path',
      'field_affiliated_us_gov',
      'field_accredited_length',
      'field_required_length',
      'field_survey',
      'field_institution',
      'field_participant_institution',
      'field_sponsor_institution',
      'drupal_internal__nid',
      'field_image',
      'path',
      'field_geolocation',
      'field_program_maintainers',
      'field_video.name',
      'field_video.path',
      'metatag_normalized',
      'field_resource_video_url_1',
      'field_resource_gallery_ref_1',
      'field_resource_gallery_ref_2',
      'field_resource_gallery_ref_3',
      'field_resource_gallery_ref_4',
      'field_resource_gallery_ref_5',
      'field_resource_facebook',
      'field_resource_instagram',
      'field_resource_twitter',
      'field_resource_program_block_ref',
      'field_resource_institutional_ref',
      'field_resource_wellness_ref',
      'field_resource_resident_wellness',
      'field_resource_develop_opps',
      'field_resource_curriculum',
      'field_resource_cases_patients',
      'field_resource_program_type',
      'field_resource_funding',
      'field_resource_top_candidate_ch',
      'field_resource_top_program_char'
    ]);

    const programs = Array.isArray(normalizedData)
      ? normalizedData.map(prgrm => ProgramModel.programFromNormalized(prgrm, baseImageUrl))
      : ProgramModel.programFromNormalized(normalizedData, baseImageUrl);

    if (Array.isArray(programs)) {
      if (programs.length > 0) {
        return programs[0];
      } else {
        throw new Error('Program not found.');
      }
    } else {
      return programs;
    }
  }

  private static programFromNormalized(normalizedProgram: any, baseImageUrl: string): ProgramModel {
    const program = new ProgramModel({
      programId: normalizedProgram.field_program_id,
      uuid: normalizedProgram.id,
      programName: normalizedProgram.title,
      programNumber: normalizedProgram.field_program_id,
      specialtyName: normalizedProgram.field_specialty ? normalizedProgram.field_specialty.title : '',
      specialtyLink: normalizedProgram.field_specialty ? normalizedProgram.field_specialty.path : '',
      isExpanded: normalizedProgram.field_expanded_listing,
      city: normalizedProgram.field_address ? normalizedProgram.field_address.locality : '',
      state: normalizedProgram.field_address ? normalizedProgram.field_address.administrative_area : '',
      field_affiliated_us_gov: normalizedProgram.field_affiliated_us_gov,
      field_accredited_length: normalizedProgram.field_accredited_length,
      field_required_length: normalizedProgram.field_required_length,
      changed: normalizedProgram.changed,
      nid: normalizedProgram.drupal_internal__nid,
      cmsPath: normalizedProgram.path,
      addressLine1: normalizedProgram.field_address ? normalizedProgram.field_address.address_line1 : '',
      addressLine2: normalizedProgram.field_address ? normalizedProgram.field_address.address_line2 : '',
      geoLocation: normalizedProgram.field_geolocation,
      metatags: normalizedProgram.metatag_normalized ? normalizedProgram.metatag_normalized : ''
    });

    if (normalizedProgram.field_program_maintainers &&
      normalizedProgram.field_program_maintainers.dataset &&
      normalizedProgram.field_program_maintainers.dataset.data) {
      normalizedProgram.field_program_maintainers.dataset.data.forEach(user => {
        const maintainer = new UserModel();
        maintainer.uuid = user.id;
        program.maintainers.push(maintainer);
      });
    }

    if (normalizedProgram.field_image && normalizedProgram.field_image.dataset.included.filter(f => f.type === 'file--file')[0]) {
      program.imageUrl = MediaPathHelper.imageSrcUrl(
        normalizedProgram.field_image.dataset.included.filter(f => f.type === 'file--file')[0].attributes.uri.url, baseImageUrl);
    }

    if (normalizedProgram.field_video) {
      program.videos = [];
      for (const videoData of normalizedProgram.field_video) {
        program.videos.push({
          title: videoData['name'],
          path: videoData['path']
        });
      }
    }

    // Pull in the two quality of life dropdown fields
    program.field_resource_video_url_1 = normalizedProgram.field_resource_video_url_1;
    program.field_resource_gallery_ref_1 = normalizedProgram.field_resource_gallery_ref_1;
    program.field_resource_gallery_ref_2 = normalizedProgram.field_resource_gallery_ref_2;
    program.field_resource_gallery_ref_3 = normalizedProgram.field_resource_gallery_ref_3;
    program.field_resource_gallery_ref_4 = normalizedProgram.field_resource_gallery_ref_4;
    program.field_resource_gallery_ref_5 = normalizedProgram.field_resource_gallery_ref_5;
    program.field_resource_facebook = normalizedProgram.field_resource_facebook;
    program.field_resource_instagram = normalizedProgram.field_resource_instagram;
    program.field_resource_twitter = normalizedProgram.field_resource_twitter;
    program.field_resource_program_block_ref = normalizedProgram.field_resource_program_block_ref;
    program.field_resource_institutional_ref = normalizedProgram.field_resource_institutional_ref;
    program.field_resource_wellness_ref = normalizedProgram.field_resource_wellness_ref;
    program.field_resource_resident_wellness = normalizedProgram.field_resource_resident_wellness;
    program.field_resource_develop_opps = normalizedProgram.field_resource_develop_opps;
    program.field_resource_curriculum = normalizedProgram.field_resource_curriculum;
    program.field_resource_cases_patients = normalizedProgram.field_resource_cases_patients;
    program.field_resource_program_type = normalizedProgram.field_resource_program_type;
    program.field_resource_funding = normalizedProgram.field_resource_funding;
    program.field_resource_top_candidate_ch = normalizedProgram.field_resource_top_candidate_ch;
    program.field_resource_top_program_char = normalizedProgram.field_resource_top_program_char;

    if (normalizedProgram.field_survey) {
      const indexOfLatestSurvey = normalizedProgram.field_survey.dataset.data.length - 1;
      const surveyDataJson = normalizedProgram.field_survey.dataset.data[indexOfLatestSurvey];

      program.surveyType = surveyDataJson.type;
      program.surveyId = surveyDataJson.id;

      const fields = [];
      Object.keys(surveyDataJson.attributes).forEach(key => {
        fields.push(new LabelValueModel({
          field: key,
          value: surveyDataJson.attributes[key]
        }));
      });
      program.survey = fields;

      if (normalizedProgram.field_survey.get('field_program_director')[0]) {
        program.director = IndividualModel
          .parseIndividual(normalizedProgram.field_survey.get('field_program_director')[0].dataset);
      }

      if (normalizedProgram.field_survey.get('field_program_codirector')[0]) {
        program.codirector = IndividualModel
          .parseIndividual(normalizedProgram.field_survey.get('field_program_codirector')[0].dataset);
      }

      if (normalizedProgram.field_survey.get('field_program_contact')[0]) {
        program.contact = IndividualModel
          .parseIndividual(normalizedProgram.field_survey.get('field_program_contact')[0].dataset);
      }
    }

    if (normalizedProgram.field_institution) {
      program.primaryInstitution = InstitutionModel.parseInstitution(normalizedProgram.field_institution.dataset, baseImageUrl) as InstitutionModel;
    }

    if (normalizedProgram.field_participant_institution) {
      program.participantInstitutions = InstitutionModel.parseInstitution(normalizedProgram
        .field_participant_institution.dataset, baseImageUrl) as InstitutionModel[];
    }

    if (normalizedProgram.field_sponsor_institution) {
      program.sponsorInstitutions = InstitutionModel.parseInstitution(normalizedProgram
        .field_sponsor_institution.dataset, baseImageUrl) as InstitutionModel;
    }

    if (normalizedProgram.field_survey
      && normalizedProgram.field_survey.get('field_primary_teaching_site')
      && !normalizedProgram.field_survey.get('field_primary_teaching_site').includes(undefined)) {
      program.primaryTeachingSite = InstitutionModel.parseInstitution(normalizedProgram
        .field_survey.get('field_primary_teaching_site')[0].dataset, baseImageUrl) as InstitutionModel;
    }

    return program;
  }

  public programId: string;
  public specialtyName: string;
  public specialtyLink: any;
  public programName: string;
  public programNumber: string;
  public city: string;
  public state: string;
  public metatags: any;

  public region: string;
  public isExpanded: boolean;
  public field_accredited_length: number;
  public field_required_length: number;
  public field_affiliated_us_gov: string;
  public changed: string;
  public imageUrl: string;
  public videos: { title: string, path: string }[];

  public survey: LabelValueModel[] = [];
  public surveyType: string;
  public surveyId: string;

  public primaryInstitution: InstitutionModel;
  public participantInstitutions: InstitutionModel[] = [];
  public sponsorInstitutions: InstitutionModel;
  public primaryTeachingSite: InstitutionModel;

  public director: IndividualModel;
  public codirector: IndividualModel;
  public contact: IndividualModel;

  public addressLine1: string;
  public addressLine2: string;
  public geoLocation?: any;

  public field_resource_video_url_1: string;
  public field_resource_gallery_ref_1: string;
  public field_resource_gallery_ref_2: string;
  public field_resource_gallery_ref_3: string;
  public field_resource_gallery_ref_4: string;
  public field_resource_gallery_ref_5: string;
  public field_resource_facebook: string;
  public field_resource_instagram: string;
  public field_resource_twitter: string;
  public field_resource_program_block_ref: string;
  public field_resource_institutional_ref: string;
  public field_resource_wellness_ref: string;
  public field_resource_resident_wellness: [];
  public field_resource_develop_opps: [];
  public field_resource_curriculum: [];
  public field_resource_cases_patients: [];
  public field_resource_program_type: [];
  public field_resource_funding: [];

  public field_resource_top_candidate_ch: [];
  public field_resource_top_program_char: [];

  public maintainers: UserModel[] = [];

  constructor(values?: Partial<ProgramModel>) {
    super(values);
    Object.assign(this, values);
  }

  public getFieldLabel(field: string): string {
    const found = this.survey.find(f =>
      f.field === field
    );
    return found === undefined ? '' : found.label;
  }

  public getFieldValue(field: string): any {
    const found = this.survey.filter(f =>
      f.field === field
    );
    if (found[0] === undefined || found[0].value === undefined) {
      return '';
    } else if (this.isValueLabelPair(found[0])) {
      return found[0].value.value;
    } else {
      return found[0].value;
    }
  }

  public getFieldPairValue(field: string): any {
    const found = this.survey.find(f => f.field === field);
    return found === undefined || found.value === undefined || found.value === null ? '' : found.value.label;
  }

  public getSurveyYear(): string {
    const splitSurveyType = this.surveyType ? this.surveyType.split('_') : [''];
    const surveyTypeYear = splitSurveyType[splitSurveyType.length - 1];
    return surveyTypeYear ? surveyTypeYear : '';
  }

  public isAffiliatedWithUSGovt(): boolean {
    return !(this.field_affiliated_us_gov === '' || this.field_affiliated_us_gov === ProgramModel.NOT_AFF_US_GOVT
      || this.field_affiliated_us_gov === null || this.field_affiliated_us_gov === undefined);
  }

  public offersAnotherTrack(): boolean {
    if (this.getFieldValue('field_special_tracks')) {
      return this.getFieldValue('field_special_tracks').filter(w => {
        return w !== 'Primary care' && w !== 'Rural' && w !== 'Academic or clinician educator' && w !== 'Women\'s health' && w !== 'Hospitalist' && w !== 'Research track/fellowship (non-ACGME accredited)';
      }).length > 0;
    }
    return false;
  }

  /**
   * Helper method to determine whether a field value is an object with both field and value properties
   * @param fieldValue
   */
  private isValueLabelPair(fieldValue: any): boolean {
    return fieldValue.value
      && fieldValue.value.value !== undefined
      && fieldValue.value.label !== undefined;
  }
}
